import { StrictMode } from 'react'
import { createRoot, type Root } from 'react-dom/client'
import { GraphWidget } from './widget/GraphWidget'
import type { GraphWidgetOptions } from './widget/types'
import { fetchGraphData } from './widget/api'

const roots = new WeakMap<Element, Root>()
const activePollStops = new WeakMap<Element, () => void>()

export type MountOptions = GraphWidgetOptions

export function mount(element: Element, options: MountOptions): void {
    const existingRoot = roots.get(element)
    if (existingRoot) {
        existingRoot.render(
            <StrictMode>
                <GraphWidget {...options} />
            </StrictMode>,
        )
        return
    }

    const root = createRoot(element)
    roots.set(element, root)
    root.render(
        <StrictMode>
            <GraphWidget {...options} />
        </StrictMode>,
    )
}

export function unmount(element: Element): void {
    const root = roots.get(element)
    if (!root) return
    root.unmount()
    roots.delete(element)
}

export type PollingMountTargetById = Omit<GraphWidgetOptions, 'apiUrl' | 'pollIntervalMs' | 'headers' | 'data'> & {
    /** DOM id attribute of the mount element. */
    id: string
}

export type PollingMountTarget = Omit<GraphWidgetOptions, 'apiUrl' | 'pollIntervalMs' | 'headers' | 'data'> & {
    element: Element
}

export type StartPollingOptions = {
    apiUrl: string
    pollIntervalMs: number
    headers?: Record<string, string>
    period?: GraphWidgetOptions['period']
    /** Default theme for targets (can be overridden per target). */
    theme?: GraphWidgetOptions['theme']
    onError?: (error: unknown) => void
}

/**
 * Poll the API once per interval and re-render multiple widgets with the same response.
 * Returns a stop function.
 */
export function startPollingMounts(targets: PollingMountTarget[], options: StartPollingOptions): () => void {
    const { apiUrl, pollIntervalMs, headers, period, theme, onError } = options

    const existingStops = new Set<() => void>()
    for (const target of targets) {
        const existing = activePollStops.get(target.element)
        if (existing) existingStops.add(existing)
    }
    for (const stop of existingStops) stop()

    let timer: number | undefined
    let inFlight = false
    let controller: AbortController | null = null
    let stopped = false

    async function tick(): Promise<void> {
        if (stopped) return
        if (inFlight) return
        inFlight = true
        try {
            controller?.abort()
            controller = new AbortController()
            const data = await fetchGraphData({ apiUrl, headers, period, signal: controller.signal })

            for (const target of targets) {
                mount(target.element, {
                    ...target,
                    apiUrl,
                    headers,
                    period,
                    theme: target.theme ?? theme,
                    data,
                })
            }
        } catch (error) {
            if (error instanceof DOMException && error.name === 'AbortError') return
            if (typeof onError === 'function') onError(error)
            else console.error('GraphWidget polling fetch failed:', error)
        } finally {
            inFlight = false
        }
    }

    void tick()
    timer = window.setInterval(() => {
        void tick()
    }, pollIntervalMs)

    const stop = () => {
        stopped = true
        if (timer) window.clearInterval(timer)
        controller?.abort()
        controller = null
        for (const target of targets) {
            const current = activePollStops.get(target.element)
            if (current === stop) activePollStops.delete(target.element)
        }
    }

    for (const target of targets) {
        activePollStops.set(target.element, stop)
    }

    return stop
}

/** Convenience wrapper for plain HTML: mount targets by element id and start polling. */
export function startPollingMountsById(targets: PollingMountTargetById[], options: StartPollingOptions): () => void {
    const resolvedTargets: PollingMountTarget[] = targets.flatMap((t) => {
        const element = document.getElementById(t.id)
        if (!element) return []
        const { id: _id, ...rest } = t
        return [{ element: element as Element, ...rest }]
    })

    return startPollingMounts(resolvedTargets, options)
}

export { GraphWidget }
export type { GraphWidgetOptions }

export { fetchGraphData }
